import jsPDF from 'jspdf';

export interface PDFExtractionOptions {
  filename: string;
  type: 'company' | 'financial-institution';
  title: string;
  subtitle?: string;
}

interface FormField {
  label: string;
  value: string | string[] | boolean | any;
  type: 'text' | 'select' | 'multiselect' | 'checkbox' | 'textarea' | 'date' | 'number' | 'email' | 'tel' | 'list' | 'complex';
  width?: number;
  options?: Array<{value: string, label: string}>;
  required?: boolean;
}

class ComprehensivePDFGenerator {
  private pdf: jsPDF;
  private pageWidth: number;
  private pageHeight: number;
  private margin = 20;
  private contentStartY = 45; // Position de départ du contenu (plus d'espace pour l'en-tête)
  private currentY = 0;
  private pageNumber = 1;

  constructor() {
    // Format A4 explicite : 210mm x 297mm
    this.pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    this.pageWidth = 210; // A4 width
    this.pageHeight = 297; // A4 height
  }

  // En-tête de page avec titre formulaire dans l'en-tête
  private addPageHeader(sectionTitle?: string, formType?: string, formStatus?: string) {
    if (this.pageNumber > 1) {
      this.pdf.addPage();
    }
    
    // Titre du formulaire dans l'en-tête (petit, discret)
    if (formType && formStatus) {
      this.pdf.setFontSize(7);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setTextColor(120, 120, 120);
      
      const formTitle = formType === 'company' 
        ? 'FORMULAIRE D\'IDENTIFICATION ENTREPRISE'
        : 'FORMULAIRE D\'IDENTIFICATION INSTITUTION FINANCIÈRE';
      
      const statusText = formStatus === 'blank' ? 'FORMULAIRE VIERGE' : 'FORMULAIRE COMPLÉTÉ';
      
      // Titre formulaire en haut au centre
      const titleWidth = this.pdf.getTextWidth(formTitle);
      this.pdf.text(formTitle, (this.pageWidth - titleWidth) / 2, 10);
      
      // Statut en dessous
      const statusWidth = this.pdf.getTextWidth(statusText);
      this.pdf.text(statusText, (this.pageWidth - statusWidth) / 2, 15);
    }
    
    // Numéro de page (coin gauche)
    this.pdf.setFontSize(7);
    this.pdf.setTextColor(150, 150, 150);
    this.pdf.text(`Page ${this.pageNumber}`, this.margin, 20);
    
    this.pageNumber++;
    this.currentY = this.contentStartY;
    
    // Titre de section principal (avec espacement suffisant)
    if (sectionTitle) {
      this.pdf.setFontSize(12);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(25, 124, 168);
      this.pdf.text(sectionTitle, this.margin, this.currentY);
      this.currentY += 10;
      
      // Ligne de séparation
      this.pdf.setDrawColor(25, 124, 168);
      this.pdf.setLineWidth(0.2);
      this.pdf.line(this.margin, this.currentY, this.pageWidth - this.margin, this.currentY);
      this.currentY += 8; // Espacement suffisant après la ligne
    }
  }

  // Footer
  private addFooter() {
    const footerY = this.pageHeight - 12;
    this.pdf.setFontSize(6);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setTextColor(120, 120, 120);
    
    this.pdf.text('© 2025 i-kiotaHub - Wanzo Land', this.margin, footerY);
    this.pdf.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 
                  this.pageWidth - this.margin - 60, footerY);
  }

  // Champ moderne avec gestion des différents types
  private drawField(field: FormField, x: number, y: number, width: number): number {
    let fieldHeight = 8;
    
    // Adapter la hauteur selon le type
    if (field.type === 'textarea') fieldHeight = 20;
    else if (field.type === 'list' || field.type === 'complex') fieldHeight = Math.max(12, Array.isArray(field.value) ? field.value.length * 4 + 8 : 8);
    else if (field.type === 'multiselect') fieldHeight = 12;
    
    // Label avec style
    this.pdf.setFontSize(8);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(60, 60, 60);
    this.pdf.text(field.label + (field.required ? ' *' : ''), x, y);
    
    // Champ avec bordure moderne
    const fieldY = y + 3;
    this.pdf.setDrawColor(180, 180, 180);
    this.pdf.setLineWidth(0.2);
    this.pdf.setFillColor(250, 250, 250);
    this.pdf.roundedRect(x, fieldY, width, fieldHeight, 1.5, 1.5, 'FD');
    
    // Valeur selon le type
    this.pdf.setFontSize(7);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setTextColor(40, 40, 40);
    
    if (field.value !== null && field.value !== undefined && field.value !== '') {
      this.renderFieldValue(field, x + 2, fieldY + 4, width - 4);
    } else {
      this.pdf.setFontSize(6);
      this.pdf.setFont('helvetica', 'italic');
      this.pdf.setTextColor(150, 150, 150);
      this.pdf.text('À compléter...', x + 2, fieldY + 4);
    }
    
    return fieldY + fieldHeight + 6;
  }

  // Rendu de la valeur selon le type de champ
  private renderFieldValue(field: FormField, x: number, y: number, maxWidth: number) {
    switch (field.type) {
      case 'checkbox':
        this.pdf.text(field.value ? '☑ Oui' : '☐ Non', x, y);
        break;
        
      case 'multiselect':
        if (Array.isArray(field.value) && field.value.length > 0) {
          const items = field.value.map(v => `• ${this.getLabelForValue(v, field.options)}`).join(', ');
          const lines = this.pdf.splitTextToSize(items, maxWidth);
          this.pdf.text(lines, x, y);
        }
        break;
        
      case 'list':
        if (Array.isArray(field.value) && field.value.length > 0) {
          field.value.forEach((item, index) => {
            if (typeof item === 'string') {
              this.pdf.text(`• ${item}`, x, y + (index * 3));
            } else {
              this.pdf.text(`• ${JSON.stringify(item)}`, x, y + (index * 3));
            }
          });
        }
        break;
        
      case 'complex':
        this.renderComplexField(field.value, x, y);
        break;
        
      case 'textarea':
        const lines = this.pdf.splitTextToSize(String(field.value), maxWidth);
        this.pdf.text(lines, x, y);
        break;
        
      case 'select':
        const selectedLabel = this.getLabelForValue(field.value, field.options);
        this.pdf.text(selectedLabel, x, y);
        break;
        
      default:
        const text = String(field.value);
        if (this.pdf.getTextWidth(text) > maxWidth) {
          const truncated = text.substring(0, Math.floor(text.length * maxWidth / this.pdf.getTextWidth(text))) + '...';
          this.pdf.text(truncated, x, y);
        } else {
          this.pdf.text(text, x, y);
        }
    }
  }

  // Rendu de champs complexes (objets, personnes, actifs, etc.)
  private renderComplexField(value: any, x: number, y: number) {
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (typeof item === 'object' && item !== null) {
          const summary = this.getObjectSummary(item);
          this.pdf.text(`${index + 1}. ${summary}`, x, y + (index * 3));
        }
      });
    } else if (typeof value === 'object' && value !== null) {
      const summary = this.getObjectSummary(value);
      this.pdf.text(summary, x, y);
    }
  }

  // Résumé d'un objet complexe
  private getObjectSummary(obj: any): string {
    if (obj.designation) return `${obj.designation} (${obj.type || 'N/A'})`;
    if (obj.nom && obj.prenoms) return `${obj.nom} ${obj.prenoms} - ${obj.fonction || 'N/A'}`;
    if (obj.platform && obj.url) return `${obj.platform}: ${obj.url}`;
    if (obj.name) return obj.name;
    if (obj.label) return obj.label;
    return 'Élément configuré';
  }

  // Obtenir le label d'une option
  private getLabelForValue(value: any, options?: Array<{value: string, label: string}>): string {
    if (!options) return String(value);
    const option = options.find(opt => opt.value === value);
    return option ? option.label : String(value);
  }

  // Section avec titre
  private drawSectionTitle(title: string): number {
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(25, 124, 168);
    this.pdf.text(title, this.margin, this.currentY);
    
    this.pdf.setDrawColor(220, 220, 220);
    this.pdf.setLineWidth(0.1);
    this.pdf.line(this.margin, this.currentY + 1, this.pageWidth - this.margin, this.currentY + 1);
    
    return this.currentY + 8;
  }

  // Vérifier saut de page
  private checkPageBreak(neededHeight: number = 25, formType?: string, formStatus?: string): void {
    if (this.currentY + neededHeight > this.pageHeight - 30) {
      this.addFooter();
      this.addPageHeader(undefined, formType, formStatus);
    }
  }

  // Page de couverture simplifiée
  private generateCoverPage(options: PDFExtractionOptions, entityName: string, isBlank: boolean) {
    this.currentY = 80;
    
    // Logo/Symbole Wanzo
    this.pdf.setFontSize(24);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(25, 124, 168);
    this.pdf.text('WANZO', (this.pageWidth - this.pdf.getTextWidth('WANZO')) / 2, this.currentY);
    
    this.currentY += 20;
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setTextColor(80, 80, 80);
    this.pdf.text('Plateforme d\'accélération d\'entreprise', (this.pageWidth - this.pdf.getTextWidth('Plateforme d\'accélération d\'entreprise')) / 2, this.currentY);
    
    // Nom de l'entité (principal)
    if (!isBlank && entityName) {
      this.currentY += 40;
      this.pdf.setFontSize(16);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(25, 124, 168);
      const nameWidth = this.pdf.getTextWidth(entityName);
      this.pdf.text(entityName, (this.pageWidth - nameWidth) / 2, this.currentY);
    }
    
    // Type de formulaire (discret)
    this.currentY += 30;
    this.pdf.setFontSize(11);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setTextColor(100, 100, 100);
    const typeText = options.type === 'company' 
      ? 'Formulaire d\'identification entreprise'
      : 'Formulaire d\'identification institution financière';
    
    const typeWidth = this.pdf.getTextWidth(typeText);
    this.pdf.text(typeText, (this.pageWidth - typeWidth) / 2, this.currentY);
    
    // Informations document
    this.currentY += 40;
    this.pdf.setFontSize(9);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setTextColor(120, 120, 120);
    
    this.pdf.text('Généré le :', this.margin + 30, this.currentY);
    this.pdf.text(new Date().toLocaleDateString('fr-FR'), this.margin + 55, this.currentY);
    
    this.pdf.text('à :', this.margin + 85, this.currentY);
    this.pdf.text(new Date().toLocaleTimeString('fr-FR'), this.margin + 95, this.currentY);
    
    // Notice encadrée
    this.currentY += 25;
    this.pdf.setFontSize(8);
    this.pdf.setTextColor(25, 124, 168);
    
    this.pdf.setDrawColor(25, 124, 168);
    this.pdf.setLineWidth(0.2);
    this.pdf.rect(this.margin, this.currentY - 3, this.pageWidth - 2 * this.margin, 25);
    
    const notice = options.type === 'company' 
      ? 'Ce formulaire doit être complété pour l\'évaluation d\'éligibilité aux services Wanzo.'
      : 'Ce formulaire permet l\'enregistrement et la collaboration avec la plateforme Wanzo.';
    
    const noticeLines = this.pdf.splitTextToSize(notice, this.pageWidth - 2 * this.margin - 6);
    this.pdf.text(noticeLines, this.margin + 3, this.currentY + 5);
    
    this.addFooter();
  }

  // Générateur ENTREPRISE complet avec tous les composants UI
  generateCompanyForm(formData: any, isBlank: boolean, options: PDFExtractionOptions): void {
    const entityName = formData?.raisonSociale || 'Entreprise';
    const getValue = (key: string) => isBlank ? null : this.getNestedValue(formData, key);
    const formType = 'company';
    const formStatus = isBlank ? 'blank' : 'complete';
    
    // Helper pour checkPageBreak avec les bons paramètres
    const checkBreak = (height: number = 25) => this.checkPageBreak(height, formType, formStatus);
    
    // Page de couverture
    this.generateCoverPage(options, entityName, isBlank);
    
    const fieldWidth = (this.pageWidth - 3 * this.margin) / 2;
    
    // ONGLET 1: GÉNÉRAL
    this.addPageHeader('ONGLET 1: INFORMATIONS GÉNÉRALES', 'company', isBlank ? 'blank' : 'complete');
    
    // 1. Identification de base
    this.currentY = this.drawSectionTitle('1. Identification de Base');
    
    const basicFields: FormField[] = [
      { label: 'Raison sociale', value: getValue('raisonSociale'), type: 'text', required: true },
      { label: 'Sigle', value: getValue('sigle'), type: 'text' },
      { label: 'Forme juridique OHADA', value: getValue('formeJuridiqueOHADA'), type: 'select', required: true, 
        options: [
          { value: 'SARL', label: 'SARL (Société À Responsabilité Limitée)' },
          { value: 'SA', label: 'SA (Société Anonyme)' },
          { value: 'SAS', label: 'SAS (Société par Actions Simplifiée)' },
        ]},
      { label: 'Date de création', value: getValue('dateCreation'), type: 'date', required: true },
      { label: 'Date début activités', value: getValue('dateDebutActivites'), type: 'date' },
      { label: 'Taille entreprise', value: getValue('tailleEntreprise'), type: 'select',
        options: [
          { value: '1-10', label: '1-10 employés' },
          { value: '11-50', label: '11-50 employés' },
          { value: '51-200', label: '51-200 employés' },
        ]},
    ];
    
    for (let i = 0; i < basicFields.length; i += 2) {
      checkBreak(15);
      const nextY1 = this.drawField(basicFields[i], this.margin, this.currentY, fieldWidth);
      let nextY2 = this.currentY;
      if (basicFields[i + 1]) {
        nextY2 = this.drawField(basicFields[i + 1], this.margin + fieldWidth + 8, this.currentY, fieldWidth);
      }
      this.currentY = Math.max(nextY1, nextY2);
    }
    
    // 2. Immatriculations
    this.checkPageBreak(20);
    this.currentY = this.drawSectionTitle('2. Immatriculations et Identifications');
    
    const regFields: FormField[] = [
      { label: 'Numéro RCCM', value: getValue('numeroRCCM'), type: 'text' },
      { label: 'Numéro identification nationale', value: getValue('numeroIdentificationNationale'), type: 'text' },
      { label: 'Numéro impôt fiscal', value: getValue('numeroImpotFiscal'), type: 'text' },
    ];
    
    for (let i = 0; i < regFields.length; i += 2) {
      this.checkPageBreak(15);
      const nextY1 = this.drawField(regFields[i], this.margin, this.currentY, fieldWidth);
      let nextY2 = this.currentY;
      if (regFields[i + 1]) {
        nextY2 = this.drawField(regFields[i + 1], this.margin + fieldWidth + 8, this.currentY, fieldWidth);
      }
      this.currentY = Math.max(nextY1, nextY2);
    }
    
    // 3. Nature des activités (avec TagInput et choix multiples)
    this.checkPageBreak(25);
    this.currentY = this.drawSectionTitle('3. Nature des Activités');
    
    const activityFields: FormField[] = [
      { label: 'Type d\'entreprise', value: getValue('typeEntreprise'), type: 'select', required: true,
        options: [
          { value: 'traditional', label: 'Entreprise traditionnelle (PME classique)' },
          { value: 'startup', label: 'Startup (entreprise technologique innovante)' }
        ]},
      { label: 'Secteur principal', value: getValue('secteurActivitePrincipal'), type: 'select', required: true },
      { label: 'Secteurs secondaires', value: getValue('secteursActiviteSecondaires'), type: 'multiselect' },
      { label: 'Secteurs personnalisés', value: getValue('secteursPersonalises'), type: 'list' },
      { label: 'Produits/Services', value: getValue('produitsServices'), type: 'list' },
    ];
    
    for (const field of activityFields) {
      this.checkPageBreak(20);
      this.currentY = this.drawField(field, this.margin, this.currentY, this.pageWidth - 2 * this.margin);
    }
    
    this.checkPageBreak(25);
    this.currentY = this.drawField(
      { label: 'Description détaillée des activités', value: getValue('descriptionActivites'), type: 'textarea', required: true },
      this.margin, this.currentY, this.pageWidth - 2 * this.margin
    );
    
    // 4. Capital social
    this.checkPageBreak(20);
    this.currentY = this.drawSectionTitle('4. Capital Social');
    
    const capitalFields: FormField[] = [
      { label: 'Montant capital social', value: getValue('capitalSocial'), type: 'number' },
      { label: 'Devise', value: getValue('deviseCapital'), type: 'select',
        options: [
          { value: 'USD', label: 'USD - Dollar américain' },
          { value: 'CDF', label: 'CDF - Franc congolais' },
          { value: 'EUR', label: 'EUR - Euro' }
        ]},
    ];
    
    const nextY1 = this.drawField(capitalFields[0], this.margin, this.currentY, fieldWidth);
    const nextY2 = this.drawField(capitalFields[1], this.margin + fieldWidth + 8, this.currentY, fieldWidth);
    this.currentY = Math.max(nextY1, nextY2);
    
    // 5. Incubation (avec conditions)
    this.checkPageBreak(25);
    this.currentY = this.drawSectionTitle('5. Incubation et Accélération');
    
    const incubationFields: FormField[] = [
      { label: 'En incubation/accélération', value: getValue('enIncubation'), type: 'checkbox' },
      { label: 'Type accompagnement', value: getValue('typeAccompagnement'), type: 'select',
        options: [
          { value: 'incubation', label: 'Incubation' },
          { value: 'acceleration', label: 'Accélération' }
        ]},
      { label: 'Nom incubateur/accélérateur', value: getValue('nomIncubateurAccelerateur'), type: 'text' },
    ];
    
    for (const field of incubationFields) {
      this.checkPageBreak(15);
      this.currentY = this.drawField(field, this.margin, this.currentY, this.pageWidth - 2 * this.margin);
    }
    
    this.addFooter();
    
    // ONGLET 2: PATRIMOINE (avec AssetInput, StockInput)
    this.addPageHeader('ONGLET 2: PATRIMOINE ET MOYENS', 'company', isBlank ? 'blank' : 'complete');
    
    this.currentY = this.drawSectionTitle('1. Immobilisations');
    this.currentY = this.drawField(
      { label: 'Biens immobiliers', value: getValue('immobilisations'), type: 'complex' },
      this.margin, this.currentY, this.pageWidth - 2 * this.margin
    );
    
    this.checkPageBreak(20);
    this.currentY = this.drawSectionTitle('2. Équipements et Matériels');
    this.currentY = this.drawField(
      { label: 'Équipements de production', value: getValue('equipements'), type: 'complex' },
      this.margin, this.currentY, this.pageWidth - 2 * this.margin
    );
    
    this.checkPageBreak(20);
    this.currentY = this.drawSectionTitle('3. Parc Automobile');
    this.currentY = this.drawField(
      { label: 'Véhicules et moyens de transport', value: getValue('vehicules'), type: 'complex' },
      this.margin, this.currentY, this.pageWidth - 2 * this.margin
    );
    
    this.checkPageBreak(20);
    this.currentY = this.drawSectionTitle('4. Stocks et Inventaires');
    this.currentY = this.drawField(
      { label: 'Stocks matières premières et produits', value: getValue('stocks'), type: 'complex' },
      this.margin, this.currentY, this.pageWidth - 2 * this.margin
    );
    
    this.checkPageBreak(20);
    this.currentY = this.drawSectionTitle('5. Moyens Techniques');
    
    const technicalFields: FormField[] = [
      { label: 'Moyens techniques et technologiques', value: getValue('moyensTechniques'), type: 'list' },
      { label: 'Capacité de production', value: getValue('capaciteProduction'), type: 'textarea' },
    ];
    
    for (const field of technicalFields) {
      this.checkPageBreak(20);
      this.currentY = this.drawField(field, this.margin, this.currentY, this.pageWidth - 2 * this.margin);
    }
    
    this.addFooter();
    
    // ONGLET 3: STRUCTURE (avec PersonInput)
    this.addPageHeader('ONGLET 3: STRUCTURE ORGANISATIONNELLE', 'company', isBlank ? 'blank' : 'complete');
    
    this.currentY = this.drawSectionTitle('1. Équipe Dirigeante');
    this.currentY = this.drawField(
      { label: 'Dirigeants et responsables', value: getValue('dirigeants'), type: 'complex' },
      this.margin, this.currentY, this.pageWidth - 2 * this.margin
    );
    
    this.checkPageBreak(20);
    this.currentY = this.drawSectionTitle('2. Structure Actionnariale');
    this.currentY = this.drawField(
      { label: 'Actionnaires et associés', value: getValue('actionnaires'), type: 'complex' },
      this.margin, this.currentY, this.pageWidth - 2 * this.margin
    );
    
    this.checkPageBreak(20);
    this.currentY = this.drawSectionTitle('3. Personnel et Employés');
    
    const staffFields: FormField[] = [
      { label: 'Nombre total d\'employés', value: getValue('nombreEmployes'), type: 'number' },
      { label: 'Employés clés', value: getValue('employes'), type: 'complex' },
      { label: 'Description organigramme', value: getValue('organigramme'), type: 'textarea' },
    ];
    
    for (const field of staffFields) {
      this.checkPageBreak(20);
      this.currentY = this.drawField(field, this.margin, this.currentY, this.pageWidth - 2 * this.margin);
    }
    
    this.addFooter();
    
    // ONGLET 4: FINANCE & JURIDIQUE (avec FinancialInput)
    this.addPageHeader('ONGLET 4: FINANCE ET JURIDIQUE', 'company', isBlank ? 'blank' : 'complete');
    
    this.currentY = this.drawSectionTitle('1. Références Bancaires');
    this.currentY = this.drawField(
      { label: 'Comptes bancaires', value: getValue('comptesBancaires'), type: 'complex' },
      this.margin, this.currentY, this.pageWidth - 2 * this.margin
    );
    
    this.checkPageBreak(20);
    this.currentY = this.drawField(
      { label: 'Financements et prêts', value: getValue('financements'), type: 'complex' },
      this.margin, this.currentY, this.pageWidth - 2 * this.margin
    );
    
    this.checkPageBreak(20);
    this.currentY = this.drawSectionTitle('2. Aspects Juridiques');
    
    const legalFields: FormField[] = [
      { label: 'Faillite antérieure', value: getValue('failliteAnterieure'), type: 'checkbox' },
      { label: 'Détails faillite', value: getValue('detailsFaillite'), type: 'textarea' },
      { label: 'Poursuites judiciaires', value: getValue('poursuiteJudiciaire'), type: 'checkbox' },
      { label: 'Détails poursuites', value: getValue('detailsPoursuites'), type: 'textarea' },
      { label: 'Garant de prêts', value: getValue('garantiePrets'), type: 'checkbox' },
      { label: 'Détails garanties', value: getValue('detailsGaranties'), type: 'textarea' },
      { label: 'Antécédents fiscaux', value: getValue('antecedentesFiscales'), type: 'checkbox' },
      { label: 'Détails antécédents', value: getValue('detailsAntecedentes'), type: 'textarea' },
    ];
    
    for (const field of legalFields) {
      this.checkPageBreak(20);
      this.currentY = this.drawField(field, this.margin, this.currentY, this.pageWidth - 2 * this.margin);
    }
    
    this.addFooter();
    
    // ONGLET 5: LOCALISATION (avec LocationPicker, SocialLinksInput)
    this.addPageHeader('ONGLET 5: LOCALISATION ET CONTACTS', 'company', isBlank ? 'blank' : 'complete');
    
    this.currentY = this.drawSectionTitle('1. Localisation des Sites');
    
    const locationFields: FormField[] = [
      { label: 'Siège social', value: getValue('siegeSocial'), type: 'complex' },
      { label: 'Siège d\'exploitation', value: getValue('siegeExploitation'), type: 'complex' },
      { label: 'Unités de production', value: getValue('unitesProduction'), type: 'complex' },
      { label: 'Points de vente', value: getValue('pointsVente'), type: 'complex' },
    ];
    
    for (const field of locationFields) {
      this.checkPageBreak(15);
      this.currentY = this.drawField(field, this.margin, this.currentY, this.pageWidth - 2 * this.margin);
    }
    
    this.checkPageBreak(20);
    this.currentY = this.drawSectionTitle('2. Informations de Contact');
    
    const contactFields: FormField[] = [
      { label: 'Téléphone fixe', value: getValue('telephoneFixe'), type: 'tel' },
      { label: 'Téléphone mobile', value: getValue('telephoneMobile'), type: 'tel' },
      { label: 'Email', value: getValue('email'), type: 'email' },
      { label: 'Fax', value: getValue('fax'), type: 'tel' },
      { label: 'Boîte postale', value: getValue('boitePostale'), type: 'text' },
    ];
    
    for (let i = 0; i < contactFields.length; i += 2) {
      this.checkPageBreak(15);
      const nextY1 = this.drawField(contactFields[i], this.margin, this.currentY, fieldWidth);
      let nextY2 = this.currentY;
      if (contactFields[i + 1]) {
        nextY2 = this.drawField(contactFields[i + 1], this.margin + fieldWidth + 8, this.currentY, fieldWidth);
      }
      this.currentY = Math.max(nextY1, nextY2);
    }
    
    this.checkPageBreak(20);
    this.currentY = this.drawSectionTitle('3. Présence Numérique');
    this.currentY = this.drawField(
      { label: 'Site web et réseaux sociaux', value: getValue('reseauxSociaux'), type: 'complex' },
      this.margin, this.currentY, this.pageWidth - 2 * this.margin
    );
    
    this.addFooter();
    
    // ONGLET 6: PITCH (avec PitchSection)
    this.addPageHeader('ONGLET 6: PITCH ET PRÉSENTATION', 'company', isBlank ? 'blank' : 'complete');
    
    this.currentY = this.drawSectionTitle('Présentation Commerciale');
    
    const pitchFields: FormField[] = [
      { label: 'Elevator Pitch', value: getValue('pitch.elevator_pitch'), type: 'textarea' },
      { label: 'Proposition de valeur', value: getValue('pitch.value_proposition'), type: 'textarea' },
      { label: 'Marché cible', value: getValue('pitch.target_market'), type: 'textarea' },
      { label: 'Avantage concurrentiel', value: getValue('pitch.competitive_advantage'), type: 'textarea' },
      { label: 'Pitch Deck (URL)', value: getValue('pitch.pitch_deck_url'), type: 'text' },
      { label: 'Vidéo démo (URL)', value: getValue('pitch.demo_video_url'), type: 'text' },
    ];
    
    for (const field of pitchFields) {
      this.checkPageBreak(25);
      this.currentY = this.drawField(field, this.margin, this.currentY, this.pageWidth - 2 * this.margin);
    }
    
    this.addFooter();
  }

  // Générateur INSTITUTION FINANCIÈRE (structure différente)
  generateInstitutionForm(formData: any, isBlank: boolean, options: PDFExtractionOptions): void {
    const entityName = formData?.denominationSociale || formData?.name || 'Institution Financière';
    const getValue = (key: string) => isBlank ? null : this.getNestedValue(formData, key);
    
    // Page de couverture
    this.generateCoverPage(options, entityName, isBlank);
    
    const fieldWidth = (this.pageWidth - 3 * this.margin) / 2;
    
    // ONGLET 1: IDENTIFICATION
    this.addPageHeader('ONGLET 1: IDENTIFICATION', 'financial-institution', isBlank ? 'blank' : 'complete');
    
    this.currentY = this.drawSectionTitle('1. Informations de Base');
    
    const basicFields: FormField[] = [
      { label: 'Dénomination sociale', value: getValue('denominationSociale'), type: 'text', required: true },
      { label: 'Sigle commercial', value: getValue('sigleCommercial'), type: 'text' },
      { label: 'Type d\'institution', value: getValue('typeInstitution'), type: 'select', required: true,
        options: [
          { value: 'BANQUE', label: 'Banque commerciale' },
          { value: 'MICROFINANCE', label: 'Institution de microfinance (IMF)' },
          { value: 'COOPEC', label: 'Coopérative d\'épargne et de crédit' },
          { value: 'FOND_GARANTIE', label: 'Fonds de garantie' },
        ]},
      { label: 'Sous-type', value: getValue('sousType'), type: 'select' },
      { label: 'Statut juridique', value: getValue('statutJuridique'), type: 'select',
        options: [
          { value: 'sa', label: 'Société Anonyme (SA)' },
          { value: 'sarl', label: 'Société À Responsabilité Limitée (SARL)' },
          { value: 'cooperative', label: 'Coopérative' },
        ]},
      { label: 'Date de création', value: getValue('dateCreation'), type: 'date', required: true },
    ];
    
    for (let i = 0; i < basicFields.length; i += 2) {
      this.checkPageBreak(15);
      const nextY1 = this.drawField(basicFields[i], this.margin, this.currentY, fieldWidth);
      let nextY2 = this.currentY;
      if (basicFields[i + 1]) {
        nextY2 = this.drawField(basicFields[i + 1], this.margin + fieldWidth + 8, this.currentY, fieldWidth);
      }
      this.currentY = Math.max(nextY1, nextY2);
    }
    
    this.checkPageBreak(20);
    this.currentY = this.drawSectionTitle('2. Immatriculations et Agréments');
    
    const regFields: FormField[] = [
      { label: 'Numéro RCCM', value: getValue('numeroRCCM'), type: 'text', required: true },
      { label: 'Numéro NIF', value: getValue('numeroNIF'), type: 'text', required: true },
      { label: 'Numéro agrément', value: getValue('numeroAgrement'), type: 'text' },
      { label: 'Autorité de supervision', value: getValue('autoriteTutelle'), type: 'select',
        options: [
          { value: 'bcc', label: 'Banque Centrale du Congo (BCC)' },
          { value: 'arca', label: 'Autorité de Régulation et de Contrôle des Assurances (ARCA)' },
          { value: 'asmf', label: 'Autorité des Services et Marchés Financiers (ASMF)' },
        ]},
    ];
    
    for (let i = 0; i < regFields.length; i += 2) {
      this.checkPageBreak(15);
      const nextY1 = this.drawField(regFields[i], this.margin, this.currentY, fieldWidth);
      let nextY2 = this.currentY;
      if (regFields[i + 1]) {
        nextY2 = this.drawField(regFields[i + 1], this.margin + fieldWidth + 8, this.currentY, fieldWidth);
      }
      this.currentY = Math.max(nextY1, nextY2);
    }
    
    this.checkPageBreak(20);
    this.currentY = this.drawSectionTitle('3. Activités Autorisées');
    this.currentY = this.drawField(
      { label: 'Services autorisés par agrément', value: getValue('activitesAutorisees'), type: 'multiselect' },
      this.margin, this.currentY, this.pageWidth - 2 * this.margin
    );
    
    this.addFooter();
    
    // ONGLET 2: LOCALISATION ET CONTACTS
    this.addPageHeader('ONGLET 2: LOCALISATION ET CONTACTS', 'financial-institution', isBlank ? 'blank' : 'complete');
    
    this.currentY = this.drawSectionTitle('1. Adresse et Localisation');
    
    const addressFields: FormField[] = [
      { label: 'Adresse complète siège', value: getValue('adresseSiege'), type: 'textarea', required: true },
      { label: 'Ville', value: getValue('ville'), type: 'text', required: true },
      { label: 'Province/État', value: getValue('province'), type: 'text' },
      { label: 'Pays', value: getValue('pays'), type: 'text', required: true },
      { label: 'Code postal', value: getValue('codePostal'), type: 'text' },
    ];
    
    for (const field of addressFields) {
      this.checkPageBreak(15);
      this.currentY = this.drawField(field, this.margin, this.currentY, this.pageWidth - 2 * this.margin);
    }
    
    this.checkPageBreak(20);
    this.currentY = this.drawSectionTitle('2. Contacts');
    
    const contactFields: FormField[] = [
      { label: 'Téléphone principal', value: getValue('telephonePrincipal'), type: 'tel', required: true },
      { label: 'Téléphone secondaire', value: getValue('telephoneSecondaire'), type: 'tel' },
      { label: 'Email principal', value: getValue('emailPrincipal'), type: 'email', required: true },
      { label: 'Email secondaire', value: getValue('emailSecondaire'), type: 'email' },
      { label: 'Site web', value: getValue('siteWeb'), type: 'text' },
      { label: 'Fax', value: getValue('fax'), type: 'tel' },
    ];
    
    for (let i = 0; i < contactFields.length; i += 2) {
      this.checkPageBreak(15);
      const nextY1 = this.drawField(contactFields[i], this.margin, this.currentY, fieldWidth);
      let nextY2 = this.currentY;
      if (contactFields[i + 1]) {
        nextY2 = this.drawField(contactFields[i + 1], this.margin + fieldWidth + 8, this.currentY, fieldWidth);
      }
      this.currentY = Math.max(nextY1, nextY2);
    }
    
    this.checkPageBreak(15);
    this.currentY = this.drawField(
      { label: 'Succursales et agences', value: getValue('succursales'), type: 'complex' },
      this.margin, this.currentY, this.pageWidth - 2 * this.margin
    );
    
    this.addFooter();
    
    // ONGLET 3: INFORMATIONS OPÉRATIONNELLES
    this.addPageHeader('ONGLET 3: INFORMATIONS OPÉRATIONNELLES', 'financial-institution', isBlank ? 'blank' : 'complete');
    
    this.currentY = this.drawSectionTitle('1. Indicateurs Financiers');
    
    const financialFields: FormField[] = [
      { label: 'Capital social (USD)', value: getValue('capitalSocial'), type: 'number', required: true },
      { label: 'Total bilan (USD)', value: getValue('totalBilan'), type: 'number' },
      { label: 'Nombre de clients', value: getValue('nombreClients'), type: 'number' },
      { label: 'Segments de clientèle', value: getValue('segmentsClientsServis'), type: 'multiselect',
        options: [
          { value: 'individuals', label: 'Particuliers' },
          { value: 'sme', label: 'PME' },
          { value: 'large_enterprises', label: 'Grandes entreprises' },
          { value: 'institutions', label: 'Institutions' },
        ]},
    ];
    
    for (const field of financialFields) {
      this.checkPageBreak(15);
      this.currentY = this.drawField(field, this.margin, this.currentY, this.pageWidth - 2 * this.margin);
    }
    
    this.checkPageBreak(20);
    this.currentY = this.drawSectionTitle('2. Services Wanzo Souhaités');
    this.currentY = this.drawField(
      { label: 'Services de crédit', value: getValue('servicesWanzoCredit'), type: 'multiselect' },
      this.margin, this.currentY, this.pageWidth - 2 * this.margin
    );
    
    this.checkPageBreak(15);
    this.currentY = this.drawField(
      { label: 'Services d\'investissement', value: getValue('servicesWanzoInvestissement'), type: 'multiselect' },
      this.margin, this.currentY, this.pageWidth - 2 * this.margin
    );
    
    this.checkPageBreak(15);
    this.currentY = this.drawField(
      { label: 'Services transactionnels', value: getValue('servicesWanzoTransactionnel'), type: 'multiselect' },
      this.margin, this.currentY, this.pageWidth - 2 * this.margin
    );
    
    this.addFooter();
    
    // ONGLET 4: PARTENARIAT ET MOTIVATION
    this.addPageHeader('ONGLET 4: PARTENARIAT ET MOTIVATION', 'financial-institution', isBlank ? 'blank' : 'complete');
    
    this.currentY = this.drawSectionTitle('1. Motivations de Partenariat');
    this.currentY = this.drawField(
      { label: 'Motivations principales', value: getValue('motivationsPartenariat'), type: 'multiselect',
        options: [
          { value: 'new_clients', label: 'Accès à de nouveaux clients' },
          { value: 'portfolio_diversification', label: 'Diversification du portefeuille' },
          { value: 'innovation_exposure', label: 'Exposition aux nouvelles technologies' },
          { value: 'ecosystem_reputation', label: 'Association à un écosystème reconnu' },
        ]},
      this.margin, this.currentY, this.pageWidth - 2 * this.margin
    );
    
    this.checkPageBreak(20);
    this.currentY = this.drawSectionTitle('2. Capacités et Engagements');
    
    const capacityFields: FormField[] = [
      { label: 'Enveloppe maximale (USD)', value: getValue('enveloppeMaximale'), type: 'number' },
      { label: 'Conditions de financement', value: getValue('conditionsFinancement'), type: 'textarea' },
      { label: 'Critères d\'éligibilité', value: getValue('criteresEligibilite'), type: 'textarea' },
      { label: 'Processus d\'approbation', value: getValue('processusApprobation'), type: 'textarea' },
    ];
    
    for (const field of capacityFields) {
      this.checkPageBreak(20);
      this.currentY = this.drawField(field, this.margin, this.currentY, this.pageWidth - 2 * this.margin);
    }
    
    this.addFooter();
  }

  // Méthode utilitaire pour accéder aux valeurs imbriquées (ex: pitch.elevator_pitch)
  private getNestedValue(obj: any, path: string): any {
    if (!obj || !path) return null;
    
    const keys = path.split('.');
    let current = obj;
    
    for (const key of keys) {
      if (current === null || current === undefined || typeof current !== 'object') {
        return null;
      }
      current = current[key];
    }
    
    return current;
  }

  // Sauvegarder le PDF
  save(filename: string): void {
    this.pdf.save(filename);
  }
}

/**
 * Fonction principale pour générer un PDF professionnel complet
 */
export const generateProfessionalPDF = (
  formData: any,
  isBlank: boolean,
  options: PDFExtractionOptions
): void => {
  const generator = new ComprehensivePDFGenerator();
  
  if (options.type === 'company') {
    generator.generateCompanyForm(formData, isBlank, options);
  } else {
    generator.generateInstitutionForm(formData, isBlank, options);
  }
  
  generator.save(options.filename);
};

/**
 * Génère le nom de fichier
 */
export const generateFileName = (
  type: 'company' | 'financial-institution',
  entityName: string,
  status: 'complete' | 'blank' = 'complete'
): string => {
  const sanitizedName = entityName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  
  const timestamp = new Date().toISOString().split('T')[0];
  const typePrefix = type === 'company' ? 'entreprise' : 'institution-financiere';
  const statusSuffix = status === 'blank' ? '-vierge' : '-complete';
  
  return `${typePrefix}-${sanitizedName}${statusSuffix}-${timestamp}.pdf`;
};